import { getToken } from "../shared/data/storage/authStorage";
import { API_BASE_URL } from "../config/api";

// ─── Types ────────────────────────────────────────────────────────────

export interface EnrichmentJob {
  id: number;
  company_id: number;
  type: "vendor" | "customer";
  file_name: string;
  total_rows: number;
  processed_rows: number;
  failed_rows: number;
  status: "uploading" | "processing" | "review_ready" | "completed";
  uploaded_by: number;
  created_at: string;
  updated_at: string;
  item_stats?: {
    total: number;
    draft: number;
    generated: number;
    confirmed: number;
    failed: number;
  };
}

export interface EnrichmentItem {
  id: number;
  job_id: number;
  type: "vendor" | "customer";
  entity_id: number;
  entity_name: string;
  default_expense_category?: string;
  default_revenue_category?: string;
  gst_applicable?: string;
  gst_rate?: number;
  recurring_billing?: string;
  repeat_business_expected?: string;
  raw_input: string;
  ai_rewritten_context?: string;
  final_context?: string;
  status: "draft" | "generated" | "confirmed" | "failed";
  retry_count: number;
  created_at: string;
  updated_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────

const getHeaders = () => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const getAuthHeader = () => {
  const token = getToken();
  return { Authorization: `Bearer ${token}` };
};

// ─── Download ─────────────────────────────────────────────────────────

const downloadFile = async (url: string, defaultName: string) => {
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeader(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Download failed: ${response.statusText}`);
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = defaultName;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+)"?/i);
    if (match) filename = match[1];
  }

  const blob = await response.blob();
  const a = document.createElement("a");
  a.href = window.URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(a.href);
  document.body.removeChild(a);
};

export const downloadVendors = () =>
  downloadFile(
    `${API_BASE_URL}/api/context-enrichment/download/vendors`,
    "vendor_context_enrichment.xlsx"
  );

export const downloadCustomers = () =>
  downloadFile(
    `${API_BASE_URL}/api/context-enrichment/download/customers`,
    "customer_context_enrichment.xlsx"
  );

// ─── Upload ───────────────────────────────────────────────────────────

const uploadFile = async (
  type: "vendors" | "customers",
  file: File
): Promise<{ job: EnrichmentJob; results: { total_rows: number; saved_rows: number; errors: Array<{ row: number; field: string; message: string }> } }> => {
  const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
  if (![".xlsx", ".xls"].includes(ext)) {
    throw new Error("Only Excel files (.xlsx, .xls) are allowed");
  }

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/upload/${type}`,
    {
      method: "POST",
      headers: getAuthHeader(),
      body: formData,
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `Upload failed: ${response.statusText}`);
  }

  return data.data;
};

export const uploadVendors = (file: File) => uploadFile("vendors", file);
export const uploadCustomers = (file: File) => uploadFile("customers", file);

// ─── Jobs ─────────────────────────────────────────────────────────────

export const listJobs = async (): Promise<EnrichmentJob[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs`,
    { headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to fetch jobs");
  return data.data || [];
};

export const getJob = async (jobId: number): Promise<EnrichmentJob> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}`,
    { headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to fetch job");
  return data.data;
};

export const getJobItems = async (
  jobId: number
): Promise<{ job: EnrichmentJob; items: EnrichmentItem[] }> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}/items`,
    { headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to fetch items");
  return data.data;
};

// ─── Actions ──────────────────────────────────────────────────────────

export const confirmAll = async (jobId: number): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}/confirm-all`,
    { method: "POST", headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to confirm all");
};

export const confirmItem = async (
  jobId: number,
  itemId: number
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}/items/${itemId}/confirm`,
    { method: "POST", headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to confirm item");
};

export const updateItem = async (
  jobId: number,
  itemId: number,
  finalContext: string
): Promise<EnrichmentItem> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}/items/${itemId}/update`,
    {
      method: "POST",
      headers: getHeaders(),
      body: JSON.stringify({ final_context: finalContext }),
    }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to update item");
  return data.data;
};

export const retryItem = async (
  jobId: number,
  itemId: number
): Promise<void> => {
  const response = await fetch(
    `${API_BASE_URL}/api/context-enrichment/jobs/${jobId}/items/${itemId}/retry`,
    { method: "POST", headers: getHeaders() }
  );
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Failed to retry item");
};
